/**
 * ConfirmarIngresoPfModal — el operador registra el ingreso del dinero del PF.
 *
 * Hilo del flujo:
 *   PF creado en pendiente_de_caja → operador recibe el dinero (efectivo,
 *   depósito bancario, o servicio al asociado / débito de su cta. corriente)
 *   → confirma acá → corre por el motor de caja del legacy: genera RIG real +
 *   movimiento_caja + movimiento_caja_pago + movimiento_cuenta del PF + activa
 *   el PF. Al terminar abre el comprobante (RIG) listo para imprimir/compartir.
 *
 * Por forma de pago pedimos exactamente lo que el motor necesita:
 *   - efectivo:                solo el monto (ya viene del capital del PF)
 *   - depósito bancario:       cuenta de Neocoop + No. boleta + fecha + monto total
 *   - servicio al asociado:    descuenta de la cuenta de Ahorro corriente del propio asociado
 *
 * (Cheque y transferencia separada quedaron fuera del MVP a pedido de Hugo —
 *  lo común en cooperativa GT es efectivo o depósito).
 */
import { useEffect, useMemo, useState } from 'react'
import { CheckCircle, X, ArrowRight, Sparkle, Printer } from '@phosphor-icons/react'
import { supabase } from '@/lib/supabase'
import { reimprimirComprobantePorMovimientoId } from '@/lib/reimprimirComprobante'
import { hoyGT } from '@/lib/fechas'

type FormaPagoKey = 'EFECTIVO' | 'DEPOSITO_BANCARIO' | 'SERVICIO_ASOCIADO'

const OPCIONES: { value: FormaPagoKey; label: string; hint: string }[] = [
  {
    value: 'EFECTIVO',
    label: 'Efectivo',
    hint: 'El asociado entregó billetes en caja.',
  },
  {
    value: 'DEPOSITO_BANCARIO',
    label: 'Depósito bancario',
    hint: 'Depositó / transfirió a una cuenta de Neocoop. Cargás la boleta.',
  },
  {
    value: 'SERVICIO_ASOCIADO',
    label: 'Servicio al asociado',
    hint: 'Se descuenta del saldo de su cuenta de Ahorro corriente y pasa al plazo.',
  },
]

type CuentaBancariaPropia = {
  id: string
  banco: string
  numero_cuenta: string
  tipo: string
  moneda: string
  alias: string | null
}

type Caja = {
  id: string
  codigo: string
  tipo: string
}

function formatQ(n: number): string {
  return `Q ${n.toLocaleString('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`
}

// "Hoy" SIEMPRE via hoyGT() (hora Guatemala) — nunca TZ del navegador.

export function ConfirmarIngresoPfModal({
  open,
  plazoFijo,
  onClose,
  onConfirmed,
}: {
  open: boolean
  plazoFijo: {
    id: string
    asociado_id: string
    capital: number
    origen_fondos: string | null
    cuenta_acreditacion_id: string | null
    certificado_no: string | null
  } | null
  onClose: () => void
  onConfirmed: () => void
}) {
  // Default según el origen elegido al crear el PF
  const defaultForma: FormaPagoKey = useMemo(() => {
    if (plazoFijo?.origen_fondos === 'DEBITO_AHORRO_CORRIENTE') return 'SERVICIO_ASOCIADO'
    if (plazoFijo?.origen_fondos === 'DEPOSITO_BANCARIO') return 'DEPOSITO_BANCARIO'
    return 'EFECTIVO'
  }, [plazoFijo?.origen_fondos])

  const [forma, setForma] = useState<FormaPagoKey>(defaultForma)

  // Catálogos
  const [cajas, setCajas] = useState<Caja[]>([])
  const [bancos, setBancos] = useState<CuentaBancariaPropia[]>([])
  const [cajaId, setCajaId] = useState<string>('')

  // Campos por forma de pago
  const [cuentaBancariaPropiaId, setCuentaBancariaPropiaId] = useState<string>('')
  const [numeroBoleta, setNumeroBoleta] = useState('')
  const [fechaBoleta, setFechaBoleta] = useState(hoyGT())
  const [montoBoletaTotal, setMontoBoletaTotal] = useState('')

  const [notas, setNotas] = useState('')

  // Cuenta de ahorro corriente del asociado (fuente real del débito para
  // "servicio al asociado"). El PF puede no tener cuenta_acreditacion_id
  // (queda null en PF al vencimiento), por eso la derivamos por asociado.
  const [ctaAhorroId, setCtaAhorroId] = useState<string | null>(null)

  const [submitting, setSubmitting] = useState(false)
  const [imprimiendo, setImprimiendo] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [exito, setExito] = useState<{
    rig: string
    capital: number
    forma: FormaPagoKey
    movimientoCajaId: string
  } | null>(null)

  useEffect(() => {
    if (!open) return
    setForma(defaultForma)
    setCajaId('')
    setCuentaBancariaPropiaId('')
    setNumeroBoleta('')
    setFechaBoleta(hoyGT())
    setMontoBoletaTotal(plazoFijo ? String(plazoFijo.capital) : '')
    setNotas('')
    setError(null)
    setExito(null)
  }, [open, defaultForma, plazoFijo])

  // Cargar cajas activas + bancos propios cuando abre
  useEffect(() => {
    if (!open) return
    let cancel = false
    ;(async () => {
      const [cajasRes, bancosRes] = await Promise.all([
        supabase
          .from('caja')
          .select('id, codigo, tipo')
          .eq('activa', true)
          .order('tipo', { ascending: true })
          .order('codigo', { ascending: true }),
        supabase
          .from('cuenta_bancaria_propia')
          .select('id, banco, numero_cuenta, tipo, moneda, alias')
          .eq('activa', true)
          .order('banco', { ascending: true }),
      ])
      if (cancel) return
      const cajasOk = (cajasRes.data ?? []) as Caja[]
      setCajas(cajasOk)
      const bancosOk = (bancosRes.data ?? []) as CuentaBancariaPropia[]
      setBancos(bancosOk)
      // Defaults razonables: primera caja + primer banco
      if (cajasOk.length > 0) setCajaId(cajasOk[0].id)
      if (bancosOk.length > 0) setCuentaBancariaPropiaId(bancosOk[0].id)
    })()
    return () => {
      cancel = true
    }
  }, [open])

  // Ahorro corriente del asociado → fuente del débito "servicio al asociado".
  useEffect(() => {
    if (!open || !plazoFijo?.asociado_id) { setCtaAhorroId(null); return }
    let cancel = false
    ;(async () => {
      const { data } = await supabase
        .from('cuenta')
        .select('cuenta_id')
        .eq('asociado_id', plazoFijo.asociado_id)
        .eq('tipo_servicio', 'AHORRO CORRIENTE')
        .eq('estado', 'activa')
        .order('created_at', { ascending: true })
        .limit(1)
        .maybeSingle()
      if (!cancel) setCtaAhorroId((data as { cuenta_id: string } | null)?.cuenta_id ?? null)
    })()
    return () => { cancel = true }
  }, [open, plazoFijo?.asociado_id])

  const requiereBancoPropio = forma === 'DEPOSITO_BANCARIO'
  const requiereBoleta = forma === 'DEPOSITO_BANCARIO'
  const esDebitoPropio = forma === 'SERVICIO_ASOCIADO'

  const debitoBloqueado = esDebitoPropio && !ctaAhorroId

  const canSubmit =
    !!plazoFijo &&
    !submitting &&
    !!cajaId &&
    !debitoBloqueado &&
    (!requiereBancoPropio || !!cuentaBancariaPropiaId) &&
    (!requiereBoleta || (!!numeroBoleta.trim() && !!fechaBoleta && Number(montoBoletaTotal) > 0))

  const onSubmit = async () => {
    if (!plazoFijo || !canSubmit) return
    setSubmitting(true)
    setError(null)
    try {
      // Construyo el array de pagos según forma — formato del motor
      // registrar_movimiento_caja (forma_pago_enum: efectivo / deposito /
      // cheque / transferencia_bancaria / transferencia_tercero).
      const monto = plazoFijo.capital
      let pagos: any[] = []
      switch (forma) {
        case 'EFECTIVO':
          pagos = [{ tipo: 'efectivo', monto }]
          break
        case 'DEPOSITO_BANCARIO':
          pagos = [
            {
              tipo: 'deposito',
              monto,
              cuenta_bancaria_propia_id: cuentaBancariaPropiaId,
              fecha_boleta: fechaBoleta,
              numero_boleta: numeroBoleta.trim(),
              monto_boleta_total: Number(montoBoletaTotal),
            },
          ]
          break
        case 'SERVICIO_ASOCIADO':
          // Patrón del RPC: tx tercero con asociado_origen_id = mismo asociado
          // → el RPC detecta y debita la cuenta de Ahorro corriente del asociado.
          pagos = [
            {
              tipo: 'transferencia_tercero',
              monto,
              asociado_origen_id: plazoFijo.asociado_id,
              notas: 'Servicio al asociado · débito de Ahorro corriente',
            },
          ]
          break
      }

      const { data, error: rpcErr } = await supabase.rpc('confirmar_ingreso_pf_admin', {
        p_plazo_fijo_id: plazoFijo.id,
        p_caja_id: cajaId,
        p_pagos: pagos,
        p_notas: notas.trim() || null,
      })
      if (rpcErr) {
        setError(rpcErr.message)
        return
      }
      setExito({
        rig: data.rig_referencia ?? '—',
        capital: Number(data.monto),
        forma: forma,
        movimientoCajaId: data.movimiento_caja_id,
      })
      // Auto-abrir el comprobante después de un beat — Hugo: "no veo en
      // ningún lado el recibo de ingresos durante el flujo de la apertura".
      if (data.movimiento_caja_id) {
        setTimeout(() => {
          void reimprimirComprobantePorMovimientoId(data.movimiento_caja_id)
        }, 350)
      }
    } catch (e: any) {
      setError(e?.message ?? 'No pudimos confirmar el ingreso.')
    } finally {
      setSubmitting(false)
    }
  }

  if (!open || !plazoFijo) return null

  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(17,17,17,0.45)',
        zIndex: 100,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 16,
      }}
      onClick={onClose}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          width: '100%',
          maxWidth: 560,
          maxHeight: '90vh',
          background: '#FFFFFF',
          borderRadius: 18,
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        {/* Header */}
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            padding: '18px 20px',
            borderBottom: '1px solid var(--border)',
          }}
        >
          <div>
            <div
              style={{
                fontSize: 11,
                color: 'var(--brand-500)',
                fontWeight: 700,
                letterSpacing: 0.4,
                textTransform: 'uppercase',
              }}
            >
              {plazoFijo.certificado_no ?? 'Plazo fijo'}
            </div>
            <div style={{ fontSize: 17, fontWeight: 700, marginTop: 2 }}>
              {exito ? 'Ingreso registrado' : 'Confirmar ingreso recibido'}
            </div>
          </div>
          <button onClick={onClose} className="btn btn-ghost" style={{ width: 36, height: 36, padding: 0 }}>
            <X size={16} />
          </button>
        </div>

        {/* Body */}
        <div style={{ padding: 20, overflowY: 'auto', flex: 1 }}>
          {exito ? (
            <ExitoCard
              rig={exito.rig}
              capital={exito.capital}
              forma={exito.forma}
              onDone={onConfirmed}
              imprimiendo={imprimiendo}
              onImprimir={async () => {
                if (!exito.movimientoCajaId || imprimiendo) return
                setImprimiendo(true)
                try {
                  await reimprimirComprobantePorMovimientoId(exito.movimientoCajaId)
                } finally {
                  setImprimiendo(false)
                }
              }}
            />
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
              {/* Resumen del PF */}
              <div
                style={{
                  display: 'flex',
                  alignItems: 'baseline',
                  justifyContent: 'space-between',
                  padding: 14,
                  borderRadius: 12,
                  background: 'rgba(240,78,35,0.04)',
                  border: '1px solid rgba(240,78,35,0.18)',
                }}
              >
                <div>
                  <div style={{ fontSize: 11, color: 'var(--n-500)', fontWeight: 600 }}>
                    Monto a registrar
                  </div>
                  <div
                    style={{
                      fontSize: 24,
                      fontWeight: 700,
                      color: 'var(--n-900)',
                      letterSpacing: -0.6,
                      fontVariantNumeric: 'tabular-nums',
                      marginTop: 2,
                    }}
                  >
                    {formatQ(plazoFijo.capital)}
                  </div>
                </div>
              </div>

              {/* Caja activa */}
              <Section title="Caja donde se registra">
                <select
                  value={cajaId}
                  onChange={(e) => setCajaId(e.target.value)}
                  style={selectStyle}
                >
                  <option value="">Seleccionar caja…</option>
                  {cajas.map((c) => (
                    <option key={c.id} value={c.id}>
                      {c.codigo} · {c.tipo === 'FISICA' ? 'Caja física' : 'Caja virtual'}
                    </option>
                  ))}
                </select>
                {cajas.length === 0 ? (
                  <div style={{ fontSize: 11, color: 'var(--n-500)', marginTop: 4 }}>
                    No hay cajas activas. Activá una caja primero.
                  </div>
                ) : null}
              </Section>

              {/* Forma de pago */}
              <Section title="¿Cómo entró el dinero?">
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                  {OPCIONES.map((opt) => {
                    const disabled =
                      opt.value === 'SERVICIO_ASOCIADO' &&
                      !ctaAhorroId
                    return (
                      <button
                        key={opt.value}
                        onClick={() => !disabled && setForma(opt.value)}
                        disabled={disabled}
                        style={{
                          display: 'flex',
                          alignItems: 'flex-start',
                          gap: 10,
                          padding: 12,
                          borderRadius: 10,
                          background:
                            forma === opt.value
                              ? 'rgba(240,78,35,0.06)'
                              : 'transparent',
                          border:
                            forma === opt.value
                              ? '1px solid rgba(240,78,35,0.30)'
                              : '1px solid var(--border)',
                          cursor: disabled ? 'not-allowed' : 'pointer',
                          opacity: disabled ? 0.5 : 1,
                          textAlign: 'left',
                          width: '100%',
                        }}
                      >
                        <div
                          style={{
                            width: 16,
                            height: 16,
                            borderRadius: 8,
                            border:
                              '1.5px solid ' +
                              (forma === opt.value ? 'var(--brand-500)' : 'var(--n-400)'),
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            flexShrink: 0,
                            marginTop: 2,
                          }}
                        >
                          {forma === opt.value ? (
                            <div
                              style={{
                                width: 7,
                                height: 7,
                                borderRadius: 4,
                                background: 'var(--brand-500)',
                              }}
                            />
                          ) : null}
                        </div>
                        <div style={{ flex: 1 }}>
                          <div style={{ fontSize: 13, fontWeight: 500, color: 'var(--n-900)' }}>
                            {opt.label}
                            {disabled ? (
                              <span style={{ fontSize: 10, color: 'var(--n-500)', marginLeft: 6 }}>
                                — sin cuenta de origen
                              </span>
                            ) : null}
                          </div>
                          <div style={{ fontSize: 11, color: 'var(--n-500)', marginTop: 2, lineHeight: 1.4 }}>
                            {opt.hint}
                          </div>
                        </div>
                      </button>
                    )
                  })}
                </div>
              </Section>

              {/* Campos específicos por forma de pago */}
              {requiereBancoPropio ? (
                <Section title="Cuenta de Neocoop receptora">
                  <select
                    value={cuentaBancariaPropiaId}
                    onChange={(e) => setCuentaBancariaPropiaId(e.target.value)}
                    style={selectStyle}
                  >
                    <option value="">Seleccionar banco…</option>
                    {bancos.map((b) => (
                      <option key={b.id} value={b.id}>
                        {b.banco} · {b.numero_cuenta} · {b.tipo} ({b.moneda})
                        {b.alias ? ` — ${b.alias}` : ''}
                      </option>
                    ))}
                  </select>
                  {bancos.length === 0 ? (
                    <div style={{ fontSize: 11, color: 'var(--n-500)', marginTop: 4 }}>
                      No hay cuentas bancarias de Neocoop registradas. Cargá una en Bancos.
                    </div>
                  ) : null}
                </Section>
              ) : null}

              {requiereBoleta ? (
                <Section title="Datos de la boleta">
                  <Field
                    label="No. de boleta"
                    value={numeroBoleta}
                    onChange={setNumeroBoleta}
                    placeholder="Ej. 4587123"
                  />
                  <Field
                    label="Fecha de la boleta"
                    type="date"
                    value={fechaBoleta}
                    onChange={setFechaBoleta}
                  />
                  <Field
                    label="Monto total de la boleta"
                    hint="Si la boleta cubre más operaciones, poné el total. El sistema reconcilia."
                    type="number"
                    value={montoBoletaTotal}
                    onChange={setMontoBoletaTotal}
                    placeholder="0.00"
                  />
                </Section>
              ) : null}

              {esDebitoPropio ? (
                <Section title="Origen del débito">
                  <div
                    style={{
                      padding: 12,
                      borderRadius: 10,
                      background: 'rgba(240,78,35,0.04)',
                      border: '1px solid rgba(240,78,35,0.18)',
                      fontSize: 12,
                      color: 'var(--n-700)',
                      lineHeight: 1.5,
                    }}
                  >
                    Se descuenta {formatQ(plazoFijo.capital)} de la cuenta de ahorro corriente{' '}
                    <strong style={{ fontFamily: 'ui-monospace, Menlo, monospace' }}>
                      {ctaAhorroId ?? '—'}
                    </strong>{' '}
                    del asociado. Si no tiene saldo suficiente, el sistema lo bloqueará.
                  </div>
                </Section>
              ) : null}

              <Section title="Notas (opcional)">
                <Field
                  label=""
                  value={notas}
                  onChange={setNotas}
                  placeholder="Cualquier observación…"
                  multiline
                />
              </Section>

              {error ? (
                <div
                  style={{
                    padding: 12,
                    borderRadius: 10,
                    background: 'rgba(163,45,45,0.08)',
                    border: '1px solid rgba(163,45,45,0.22)',
                    fontSize: 12,
                    color: '#A32D2D',
                  }}
                >
                  {error}
                </div>
              ) : null}
            </div>
          )}
        </div>

        {/* Footer */}
        {!exito ? (
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'flex-end',
              gap: 10,
              padding: '14px 20px',
              borderTop: '1px solid var(--border)',
              background: 'var(--n-50)',
            }}
          >
            <button onClick={onClose} className="btn btn-ghost" disabled={submitting}>
              Cancelar
            </button>
            <button
              onClick={onSubmit}
              disabled={!canSubmit}
              className="btn btn-primary"
              style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}
            >
              {submitting ? 'Confirmando…' : 'Confirmar y activar plazo'}
              {!submitting ? <CheckCircle size={13} weight="bold" /> : null}
            </button>
          </div>
        ) : null}
      </div>
    </div>
  )
}

const selectStyle: React.CSSProperties = {
  display: 'block',
  width: '100%',
  padding: '8px 10px',
  border: '1px solid var(--border)',
  borderRadius: 8,
  fontSize: 13,
  background: '#fff',
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div>
      {title ? (
        <div
          style={{
            fontSize: 11,
            color: 'var(--n-500)',
            fontWeight: 700,
            letterSpacing: 0.4,
            textTransform: 'uppercase',
            marginBottom: 10,
          }}
        >
          {title}
        </div>
      ) : null}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>{children}</div>
    </div>
  )
}

function Field({
  label,
  hint,
  value,
  onChange,
  placeholder,
  multiline,
  type = 'text',
}: {
  label: string
  hint?: string
  value: string
  onChange: (v: string) => void
  placeholder?: string
  multiline?: boolean
  type?: 'text' | 'date' | 'number'
}) {
  return (
    <div>
      {label ? (
        <label style={{ fontSize: 12, color: 'var(--n-700)', fontWeight: 500 }}>{label}</label>
      ) : null}
      {multiline ? (
        <textarea
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder={placeholder}
          rows={2}
          style={{
            display: 'block',
            width: '100%',
            marginTop: label ? 5 : 0,
            padding: '8px 10px',
            border: '1px solid var(--border)',
            borderRadius: 8,
            fontSize: 13,
            fontFamily: 'inherit',
            resize: 'vertical',
          }}
        />
      ) : (
        <input
          type={type}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder={placeholder}
          style={{
            display: 'block',
            width: '100%',
            marginTop: label ? 5 : 0,
            padding: '8px 10px',
            border: '1px solid var(--border)',
            borderRadius: 8,
            fontSize: 13,
          }}
        />
      )}
      {hint ? (
        <div style={{ fontSize: 10, color: 'var(--n-500)', marginTop: 4, lineHeight: 1.4 }}>
          {hint}
        </div>
      ) : null}
    </div>
  )
}

function ExitoCard({
  rig,
  capital,
  forma,
  onDone,
  onImprimir,
  imprimiendo,
}: {
  rig: string
  capital: number
  forma: FormaPagoKey
  onDone: () => void
  onImprimir: () => void
  imprimiendo: boolean
}) {
  const formaLabel = useMemo(() => {
    const map: Record<FormaPagoKey, string> = {
      EFECTIVO: 'Efectivo',
      DEPOSITO_BANCARIO: 'Depósito bancario',
      SERVICIO_ASOCIADO: 'Servicio al asociado',
    }
    return map[forma]
  }, [forma])

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 12,
          padding: 14,
          borderRadius: 12,
          background: 'rgba(15,138,87,0.10)',
        }}
      >
        <Sparkle size={22} weight="duotone" color="#0F8A57" />
        <div style={{ flex: 1 }}>
          <div style={{ fontWeight: 600 }}>Plazo fijo activo.</div>
          <div style={{ fontSize: 12, color: 'var(--n-700)', marginTop: 2 }}>
            El capital se acreditó. Ya empieza a generar intereses según el calendario.
          </div>
        </div>
      </div>

      <table style={{ width: '100%', fontSize: 13 }}>
        <tbody>
          <tr>
            <td style={{ color: 'var(--n-500)', padding: '6px 0', fontSize: 12 }}>Forma de pago</td>
            <td style={{ textAlign: 'right', padding: '6px 0', fontWeight: 500 }}>{formaLabel}</td>
          </tr>
          <tr>
            <td style={{ color: 'var(--n-500)', padding: '6px 0', fontSize: 12 }}>RIG</td>
            <td
              style={{
                textAlign: 'right',
                padding: '6px 0',
                fontFamily: 'ui-monospace, Menlo, monospace',
                fontWeight: 500,
              }}
            >
              {rig}
            </td>
          </tr>
          <tr>
            <td style={{ color: 'var(--n-500)', padding: '6px 0', fontSize: 12 }}>Monto</td>
            <td
              style={{
                textAlign: 'right',
                padding: '6px 0',
                fontWeight: 700,
                color: '#0F8A57',
                fontVariantNumeric: 'tabular-nums',
              }}
            >
              {formatQ(capital)}
            </td>
          </tr>
        </tbody>
      </table>

      {/* Acción primaria: imprimir/compartir el comprobante.
          Hugo: "no veo en ningún lado el recibo de ingresos durante el flujo
          de la apertura". Lo abrimos automáticamente al confirmar y dejamos
          un botón explícito por si se cerró la pestaña. */}
      <button
        onClick={onImprimir}
        disabled={imprimiendo}
        className="btn btn-primary"
        style={{
          marginTop: 8,
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 6,
        }}
      >
        <Printer size={14} weight="bold" />
        {imprimiendo ? 'Abriendo…' : 'Imprimir comprobante'}
      </button>
      <button
        onClick={onDone}
        className="btn btn-ghost"
        style={{
          marginTop: 4,
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 6,
        }}
      >
        Ver plazo fijo
        <ArrowRight size={13} weight="bold" />
      </button>
    </div>
  )
}
